/**
 * "Generate Abby's voice" (spec 2026-10-23 §5 Tab 2): one progress row per
 * paragraph (Waiting / Generating / Ready / Ready — reused / Failed + Try
 * again), then one "joining" step per language. The work is the engine in
 * lib/voice-generation.ts (at most 3 paragraphs at a time); this file draws it.
 *
 * Accessibility: Generate is disabled while a run is going on, and the reason
 * is always written under it. A polite live region, present from the first
 * render, says the progress in quarter steps and then the result. When the run
 * ends, focus moves to the result ONLY if focus is on nothing or already in
 * this panel (shouldTakeFocus, the rule of the upload panel); anywhere else
 * the admin is doing something, and the live region tells them instead.
 *
 * The green "ready" message shows only while the voice still matches the text
 * (`voiceCurrent`): after an edit, an old run must not say "ready". "Try
 * again" disappears when the retry starts, so it first moves focus to its row
 * (or to the language heading for a join), never leaving focus on the page.
 */
import { useEffect, useRef } from 'react';
import { CheckCircle2, Clock, Loader2, Mic, RotateCcw, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LANGUAGE_NAME, LOCALES } from '../lib/intro-state';
import { shouldTakeFocus } from '../lib/upload-flow';
import {
  progressText,
  rowsOf,
  spokenGenerationText,
  statusLabel,
  type GenerationSnapshot,
  type LocaleRun,
  type ParagraphStatus,
} from '../lib/voice-generation';
import type { VoicePlan } from '../lib/welcome-state';
import type { ExperienceLocale } from '../lib/contract';

interface Props {
  plan: VoicePlan;
  snapshot: GenerationSnapshot;
  /** Read-only admin: nothing can start. */
  disabled: boolean;
  onGenerate: () => void;
  onRetry: (locale: ExperienceLocale, index: number) => void;
  onRetryJoin: (locale: ExperienceLocale) => void;
  /** Abby's voice matches the current text in both languages (and it is not the built-in text). */
  voiceCurrent: boolean;
  /** Start of the element ids. */
  idPrefix: string;
}

const READY_TEXT = "Abby's voice is ready. Listen to it below. Then publish.";

function StatusIcon({ status }: { status: ParagraphStatus }) {
  const cls = 'h-4 w-4 shrink-0';
  if (status === 'generating') return <Loader2 className={cn(cls, 'animate-spin motion-reduce:animate-none')} aria-hidden="true" />;
  if (status === 'failed') return <XCircle className={cn(cls, 'text-red-700 dark:text-red-400')} aria-hidden="true" />;
  if (status === 'queued') return <Clock className={cn(cls, 'text-muted-foreground')} aria-hidden="true" />;
  return <CheckCircle2 className={cn(cls, 'text-green-700 dark:text-green-400')} aria-hidden="true" />;
}

function phaseText(r: LocaleRun): string {
  if (r.phase === 'assembling') return 'Joining the paragraphs into one recording…';
  if (r.phase === 'ready') return 'Ready.';
  if (r.phase === 'failed') return r.error ?? 'Not ready.';
  return '';
}

export function VoiceGenerator({ plan, snapshot, disabled, onGenerate, onRetry, onRetryJoin, voiceCurrent, idPrefix }: Props) {
  const ids = `${idPrefix}-voice`;
  const panelRef = useRef<HTMLElement>(null);
  const resultRef = useRef<HTMLParagraphElement>(null);
  const wasRunning = useRef(false);
  const runs = LOCALES.filter((l) => snapshot.locales[l] !== undefined);
  const allReady = runs.length > 0 && runs.every((l) => snapshot.locales[l]!.phase === 'ready');
  const running = snapshot.running;
  const showReady = allReady && !running && voiceCurrent;
  const rowId = (loc: ExperienceLocale, i: number) => `${ids}-${loc}-row-${i + 1}`;
  const headingId = (loc: ExperienceLocale) => `${ids}-${loc}-heading`;
  const focusRow = (loc: ExperienceLocale, i: number) => document.getElementById(rowId(loc, i))?.focus();
  const focusHeading = (loc: ExperienceLocale) => document.getElementById(headingId(loc))?.focus();

  useEffect(() => {
    const ended = wasRunning.current && !running;
    wasRunning.current = running;
    if (ended && showReady && shouldTakeFocus(document.activeElement, document.body, panelRef.current)) {
      resultRef.current?.focus();
    }
  }, [running, showReady]);

  const reason = disabled
    ? 'You can only view this page.'
    : running
      ? "Wait until Abby's voice is ready."
      : (plan.blocked ?? plan.note);
  const canGenerate = !disabled && !running && plan.blocked === null && plan.locales.length > 0;

  return (
    <section ref={panelRef} aria-labelledby={`${ids}-heading`} className="space-y-4 rounded-lg border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h4 id={`${ids}-heading`} className="flex items-center gap-2 text-base font-semibold">
            <Mic className="h-4 w-4" aria-hidden="true" />
            Abby&apos;s voice
          </h4>
          <p className="text-sm text-muted-foreground">
            Abby reads your text aloud, one paragraph at a time. Listen to the result before you publish.
          </p>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={!canGenerate}
          aria-describedby={`${ids}-reason`}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Mic className="h-4 w-4" aria-hidden="true" />
          Generate Abby&apos;s voice
        </button>
      </div>
      <p id={`${ids}-reason`} className={cn('text-xs', plan.blocked && !running ? 'text-red-700 dark:text-red-400' : 'text-muted-foreground')}>
        {reason}
      </p>

      <p className="sr-only" aria-live="polite">
        {allReady && !running && !voiceCurrent ? '' : spokenGenerationText(snapshot)}
      </p>

      {runs.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium">{progressText(snapshot)}</p>
          <div className="grid gap-4 xl:grid-cols-2">
            {runs.map((loc) => {
              const run = snapshot.locales[loc]!;
              const name = LANGUAGE_NAME[loc];
              const phase = phaseText(run);
              return (
                <div key={loc} className="space-y-2 rounded-md border p-3">
                  <h5 id={headingId(loc)} tabIndex={-1} className="text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary">
                    {name}
                  </h5>
                  <ol className="space-y-1.5">
                    {rowsOf(snapshot, loc).map((row, i) => (
                      <li key={i} id={rowId(loc, i)} tabIndex={-1} className="rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                        <div className="flex items-center gap-2">
                          <StatusIcon status={row.status} />
                          <span className="shrink-0 font-medium">Paragraph {i + 1}</span>
                          <span className="shrink-0">— {statusLabel(row.status)}</span>
                          <span className="min-w-0 truncate text-xs text-muted-foreground" aria-hidden="true">
                            {row.text}
                          </span>
                        </div>
                        {row.status === 'failed' && (
                          <div className="ml-6 mt-1 flex flex-wrap items-center gap-2 text-xs text-red-800 dark:text-red-300">
                            <span>{row.error}</span>
                            {!disabled && (
                              <button
                                type="button"
                                onClick={() => {
                                  focusRow(loc, i);
                                  onRetry(loc, i);
                                }}
                                aria-label={`Try paragraph ${i + 1} of the ${name} text again`}
                                className="inline-flex items-center gap-1 rounded-md border border-current px-2 py-0.5 font-medium"
                              >
                                <RotateCcw className="h-3 w-3" aria-hidden="true" />
                                Try again
                              </button>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                  {phase && (
                    <div className={cn('flex flex-wrap items-center gap-2 text-xs', run.phase === 'failed' ? 'text-red-800 dark:text-red-300' : 'text-muted-foreground')}>
                      <span>{phase}</span>
                      {run.phase === 'failed' && run.failure === 'join' && !disabled && (
                        <button
                          type="button"
                          onClick={() => {
                            focusHeading(loc);
                            onRetryJoin(loc);
                          }}
                          aria-label={`Join the ${name} paragraphs again`}
                          className="inline-flex items-center gap-1 rounded-md border border-current px-2 py-0.5 font-medium"
                        >
                          <RotateCcw className="h-3 w-3" aria-hidden="true" />
                          Try again
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {showReady && (
            <p
              ref={resultRef}
              tabIndex={-1}
              className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800 focus:outline-none focus:ring-2 focus:ring-primary dark:border-green-800 dark:bg-green-950 dark:text-green-200"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              {READY_TEXT}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
